import { getAppBaseUrl } from '@/utils/app-base-url';
import { getAccessToken } from './api-client';

export interface Cohort {
  _id: string;
  name: string;
  class: string;
  board: string;
  language: string;
  exam: string;
  stream?: string;
  status: string;
  filters: {
    classes: string[];
    boards: string[];
    streams: string[];
    languages: string[];
    status: string[];
    studyMode: string[];
    courses: any[];
    batchDuration: string[];
    subExam: string[];
  };
  programId: {
    _id: string;
    name: string;
    class: string;
    language: string;
    board: string;
    exam: string[];
    organizationId: string;
    price: number;
    isQuestionBank: boolean;
    shortDescription: string;
    description: string;
    isSpecial: boolean;
    totalSubjects: number;
    totalChapters: number;
    totalTopics: number;
    totalSubTopics: number;
    totalConcepts: number;
    totalFlashCards: number;
    totalLectures: number;
    totalExercises: number;
    totalPodcast: number;
    displayOrder: number;
    status: string;
    slug: string;
    isKhazana: boolean;
    countryGroup: string;
    isPitara: boolean;
    isVitalsEnabled: boolean;
    isSubjective: boolean;
    isClinicalCornerEnabled: boolean;
    createdAt: string;
    updatedAt: string;
    __v: number;
    previewVideo: any;
    previewVideoUrl: string;
  };
  organizationId: string;
  previewTextTitle?: string;
  previewTextDescription?: string;
  icon?: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface CohortsResponse {
  data: Cohort[];
  message: string;
  success: boolean;
}

export class CohortApiService {
  private static readonly BASE_URL =
    process.env.NEXT_PUBLIC_CJR_SERVICE_BASE_URL || 'https://curiousjr-k8-api.penpencil.co';

  static async getAllCohorts(): Promise<CohortsResponse> {
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      const appBase = getAppBaseUrl();
      const response = await fetch(`${this.BASE_URL}/v1/public/cohort/all`, {
        method: 'GET',
        headers: {
          accept: '*/*',
          'accept-language': 'en-US,en;q=0.9',
          authorization: `Bearer ${token}`,
          'cache-control': 'no-cache',
          'content-type': 'application/json',
          origin: appBase,
          pragma: 'no-cache',
          priority: 'u=1, i',
          randomid: crypto.randomUUID(),
          referer: appBase ? `${appBase.replace(/\/?$/, '')}/` : '',
          'sec-ch-ua': '"Chromium";v="140", "Not=A?Brand";v="24", "Google Chrome";v="140"',
          'sec-ch-ua-mobile': '?1',
          'sec-ch-ua-platform': '"Android"',
          'sec-fetch-dest': 'empty',
          'sec-fetch-mode': 'cors',
          'sec-fetch-site': 'cross-site',
          'user-agent':
            'Mozilla/5.0 (Linux; Android 6.0; Nexus 5 Build/MRA58N) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/140.0.0.0 Mobile Safari/537.36',
          'x-device-browser': 'Mobile Chrome 140.0.0.0',
          'x-device-manufacturer': 'LG',
          'x-device-model': 'Nexus 5',
          'x-device-os': 'Android 6.0',
          'x-request-platform': 'ANDROID',
          'x-request-source': 'undefined',
          'x-request-source-link': `${appBase}/home`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching cohorts:', error);
      throw error;
    }
  }

  static async getCohortsByCountryId(countryId: string): Promise<CohortsResponse> {
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      const appBase = getAppBaseUrl();
      const response = await fetch(`${this.BASE_URL}/v1/public/cohort/all?countryId=${countryId}`, {
        method: 'GET',
        headers: {
          accept: '*/*',
          'accept-language': 'en-US,en;q=0.9',
          authorization: `Bearer ${token}`,
          'cache-control': 'no-cache',
          'content-type': 'application/json',
          origin: appBase,
          pragma: 'no-cache',
          priority: 'u=1, i',
          randomid: crypto.randomUUID(),
          referer: appBase ? `${appBase.replace(/\/?$/, '')}/` : '',
          'sec-ch-ua': '"Chromium";v="140", "Not=A?Brand";v="24", "Google Chrome";v="140"',
          'sec-ch-ua-mobile': '?1',
          'sec-ch-ua-platform': '"Android"',
          'sec-fetch-dest': 'empty',
          'sec-fetch-mode': 'cors',
          'sec-fetch-site': 'cross-site',
          'user-agent':
            'Mozilla/5.0 (Linux; Android 6.0; Nexus 5 Build/MRA58N) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/140.0.0.0 Mobile Safari/537.36',
          'x-device-browser': 'Mobile Chrome 140.0.0.0',
          'x-device-manufacturer': 'LG',
          'x-device-model': 'Nexus 5',
          'x-device-os': 'Android 6.0',
          'x-request-platform': 'ANDROID',
          'x-request-source': 'undefined',
          'x-request-source-link': `${appBase}/home`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching cohorts:', error);
      throw error;
    }
  }
}
